// IndexedDB cache for IntelSignal rows.
//
// DB:    eidollon-intel
// Store: intel-events  (key = signal.id)
// Index: by_occurred_at (signal.occurred_at, descending reads)
//
// Capacity: keep the 1000 most recent rows. On insert past cap, cull the
// oldest by occurred_at. The cap is per-tenant in practice because RLS
// guarantees a JWT session never sees signals from another tenant — but the
// cache itself is keyed by signal id only and is wiped on logout (the
// terminal calls `wipe()` from the auth client teardown path).
//
// Phase B-3 caps complexity: no migrations, no schema versioning beyond the
// initial open. When the store shape changes we bump DB_VERSION and the
// upgrade path drops + recreates the object store (events are re-hydrated
// from SSE within seconds anyway).

import type { intel as i } from '@eidollon/contracts';

export const DB_NAME = 'eidollon-intel';
// Bump after any bulk server-side change to signal content so every client
// drops + re-hydrates its cache (the store is keyed by id and the feed only
// refreshes the ~200 most recent rows, so stale rows otherwise persist).
//   v2 (2026-06-04): GDELT summaries rewritten ("GDELT event 123" → readable).
//   v3 (2026-06-04): GDELT geom fix — 159k bad-coordinate rows nulled (ocean
//                    dots); flush so cached bad-geom rows stop plotting.
export const DB_VERSION = 3;
export const STORE = 'intel-events';
export const META_STORE = 'intel-meta';
export const CAP = 1000;
const META_LAST_EVENT_ID = 'last_event_id';

export interface IntelCache {
  put(signal: i.IntelSignal): Promise<void>;
  putMany(signals: i.IntelSignal[]): Promise<void>;
  /** Most-recent first. `limit` defaults to CAP. */
  recent(limit?: number): Promise<i.IntelSignal[]>;
  size(): Promise<number>;
  /** Highest event id observed, persisted across reloads. */
  getLastEventId(): Promise<string | null>;
  setLastEventId(id: string): Promise<void>;
  wipe(): Promise<void>;
  close(): void;
}

export interface IntelCacheOptions {
  /** Override for tests (fake-indexeddb). */
  factory?: IDBFactory;
}

export async function openIntelCache(opts: IntelCacheOptions = {}): Promise<IntelCache> {
  const factory = opts.factory ?? (typeof indexedDB !== 'undefined' ? indexedDB : null);
  if (!factory) throw new Error('IndexedDB not available in this environment');

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = factory.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const upDb = req.result;
      // On any version bump, drop + recreate the events store so stale cached
      // rows (e.g. summaries changed by a server-side backfill) cannot survive
      // the upgrade — events re-hydrate from the server on next connect. The
      // META store (SSE resume cursor) is preserved so we don't re-pull history.
      if (upDb.objectStoreNames.contains(STORE)) {
        upDb.deleteObjectStore(STORE);
      }
      const os = upDb.createObjectStore(STORE, { keyPath: 'id' });
      os.createIndex('by_occurred_at', 'occurred_at', { unique: false });
      if (!upDb.objectStoreNames.contains(META_STORE)) {
        upDb.createObjectStore(META_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('idb open failed'));
  });

  function tx(stores: string | string[], mode: IDBTransactionMode) {
    return db.transaction(stores, mode);
  }

  async function size(): Promise<number> {
    return new Promise((resolve, reject) => {
      const req = tx(STORE, 'readonly').objectStore(STORE).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async function cullToCap(): Promise<void> {
    const total = await size();
    const overflow = total - CAP;
    if (overflow <= 0) return;
    await new Promise<void>((resolve, reject) => {
      const t = tx(STORE, 'readwrite');
      const ix = t.objectStore(STORE).index('by_occurred_at');
      let removed = 0;
      const req = ix.openCursor(null, 'next'); // ascending = oldest first
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor || removed >= overflow) return;
        cursor.delete();
        removed += 1;
        cursor.continue();
      };
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  }

  async function put(signal: i.IntelSignal): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const t = tx(STORE, 'readwrite');
      t.objectStore(STORE).put(signal);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
    await cullToCap();
  }

  async function putMany(signals: i.IntelSignal[]): Promise<void> {
    if (signals.length === 0) return;
    await new Promise<void>((resolve, reject) => {
      const t = tx(STORE, 'readwrite');
      const os = t.objectStore(STORE);
      for (const s of signals) os.put(s);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
    await cullToCap();
  }

  async function recent(limit = CAP): Promise<i.IntelSignal[]> {
    return new Promise((resolve, reject) => {
      const t = tx(STORE, 'readonly');
      const ix = t.objectStore(STORE).index('by_occurred_at');
      const out: i.IntelSignal[] = [];
      const req = ix.openCursor(null, 'prev'); // descending = newest first
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor || out.length >= limit) return resolve(out);
        out.push(cursor.value as i.IntelSignal);
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }

  async function getLastEventId(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const req = tx(META_STORE, 'readonly').objectStore(META_STORE).get(META_LAST_EVENT_ID);
      req.onsuccess = () => {
        const v = req.result;
        resolve(typeof v === 'string' ? v : null);
      };
      req.onerror = () => reject(req.error);
    });
  }

  async function setLastEventId(id: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const t = tx(META_STORE, 'readwrite');
      t.objectStore(META_STORE).put(id, META_LAST_EVENT_ID);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  }

  async function wipe(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const t = tx([STORE, META_STORE], 'readwrite');
      t.objectStore(STORE).clear();
      t.objectStore(META_STORE).clear();
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  }

  return {
    put,
    putMany,
    recent,
    size,
    getLastEventId,
    setLastEventId,
    wipe,
    close: () => db.close(),
  };
}
